import styled from '@emotion/styled'
import { DevSignalActions } from 'api/DevSignalActions'
import { Locale, Translation } from 'locale'
import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { canSendApplicationSignalsSelector, canTriggerSupportActionsSelector } from 'selectors/permissionSelectors'
import { GridId } from 'store/grid'
import { NotificationActions, ShowDialogArgs } from 'store/notification'
import { SelectionActions } from 'store/selection'
import { ButtonStrip, TextButton, TextButtonType } from 'view/components/buttons'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import * as constants from 'view/styles/constants'
import { PermissionsReportDialog } from './PermissionsReportDialog'
import { PurgeEntityDialog } from './PurgeEntityDialog'
import { SignalType } from 'api'
import { createAllHolidayDatesSelector } from 'selectors/holidaySelectors'
import { DateLike } from 'utils'
import { getNextAvailableBusinessDate } from './utils'

export interface OrderRecordKeepingSettingsProps extends ModalConsumerProps {
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
  setCurrentGridId: (gridId: GridId | null) => unknown
  canTriggerSupportActions: boolean
  canSendApplicationSignals: boolean
  allHolidayDates: DateLike[]
}

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog,
  setCurrentGridId: SelectionActions.setCurrentGridId
}

const Row = styled.div`
  display: flex;
  margin: 20px;
`

const Column = styled.div`
  &:not(:last-child) {
    padding-right: 20px;
    margin-right: 20px;
    border-right: 2px solid ${constants.CORE_PRIMARY_4};
  }

  & > *:not(:last-child) {
    margin-bottom: 20px;
  }
`

export const StatusIndicator = styled.span<{ color: string }>`
  margin-left: 12px;
  color: ${props => props.color};
  text-transform: uppercase;
  font-weight: ${constants.FontWeight.Bold};
  font-size: 14px;
`

const Container = styled.div``

export const BigButton = styled(TextButton)`
  font-size: 14px;
  height: 54px;
  width: 160px;
`

const mapStateToProps = createStructuredSelector({
  canTriggerSupportActions: canTriggerSupportActionsSelector,
  canSendApplicationSignals: canSendApplicationSignalsSelector,
  allHolidayDates: createAllHolidayDatesSelector
})

export class OrderRecordKeepingSettingsComponent extends React.Component<OrderRecordKeepingSettingsProps> {
  componentDidMount() {
    this.props.setCurrentGridId(null)
  }

  render() {
    return (
      <Container>
        <Row>
          <Column>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionOrkDump'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerOrkDump}>
                {Locale.translate(Translation.TC_ACTION_ORDER_RECORD_KEEPING_DUMP)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionTransactionReportingDump'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerTransactionReportingDump}>
                {Locale.translate(Translation.TC_ACTION_TRANSACTION_REPORTING_DUMP)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionTransactionReportingDump'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerNegotiationExportDump}>
                {Locale.translate(Translation.TC_ACTION_NEGOTIATION_EXPORT_DUMP)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionTransactionReportingDump'}
                type={TextButtonType.Primary}
                onClick={this.handlePermissionReport}>
                {Locale.translate(Translation.TC_ACTION_DEAL_EXPORT_DUMP)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionPermissionReportingDump'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerDealExportDump}>
                {Locale.translate(Translation.TC_ACTION_PERMISSIONS_REPORT)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionRefinitivImportReportingDump'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerRefinitivImportReport}>
                {Locale.translate(Translation.TC_ACTION_REFINITIV_IMPORT_REPORT)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionTriggerRefinitivCollateralRequest'}
                type={TextButtonType.Primary}
                onClick={this.handleRefinitivCollateralRequest}>
                {Locale.translate(Translation.TC_ACTION_TRIGGER_REFINITIV_COLLATERAL_REQUEST)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionGenerateFirdsReport'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerFirdsReport}>
                {Locale.translate(Translation.TC_ACTION_TRIGGER_FIRDS_REPORT)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionGenerateOktaUsersReport'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerOktaUsersReport}>
                {Locale.translate(Translation.TC_ACTION_TRIGGER_OKTA_USERS_REPORT)}
              </BigButton>
            </ButtonStrip>
            <ButtonStrip>
              <BigButton
                dataTestId={'OrkSettings-BigButton-ActionUpdateMortgagesFactors'}
                type={TextButtonType.Primary}
                onClick={this.handleTriggerUpdateMortgageFactors}>
                {Locale.translate(Translation.TC_ACTION_TRIGGER_UPDATE_MORTGAGE_FACTORS)}
              </BigButton>
            </ButtonStrip>
            {this.props.canTriggerSupportActions && (
              <>
                <ButtonStrip>
                  <BigButton dataTestId={'OrkSettings-BigButton-ActionSotw'} type={TextButtonType.Primary} onClick={this.handleTriggerSotw}>
                    {Locale.translate(Translation.TC_ACTION_SOTW)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionTriggerBondNoteUpdate'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerBondNoteUpdate}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_BOND_NOTE_UPDATE)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionTriggerIncrementIds'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerIncrementIds}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_INCREMENT_IDS)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionSotw'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerCalculateEndConsideration}>
                    {Locale.translate(Translation.TC_ACTION_CALCULATE_END_CONSIDERATION)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionSotw'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerCompleteDeals}>
                    {Locale.translate(Translation.TC_ACTION_COMPLETE_DEALS)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionSotw'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerFailedExportsRetry}>
                    {Locale.translate(Translation.TC_ACTION_FAILED_EXPORTS_RETRY)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionSotw'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerFailedExportsDelete}>
                    {Locale.translate(Translation.TC_ACTION_FAILED_EXPORTS_DELETE)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionTriggerRefinitivRicCodeRequest'}
                    type={TextButtonType.Primary}
                    onClick={this.handleRefinitivRicCodeRequest}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_REFINITIV_RIC_CODE_REQUEST)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionTriggerRefinitivIssuerOrgRefreshRequest'}
                    type={TextButtonType.Primary}
                    onClick={this.handleRefinitivIssuerOrgRefreshRequest}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_REFINITIV_ISSUER_REFRESH_REQUEST)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionTriggerRefinitivNewFieldsRequest'}
                    type={TextButtonType.Primary}
                    onClick={this.handleRefinitivCollateralNewFieldsRequest}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_REFINITIV_NEW_FIELDS_REQUEST)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionTriggerRefinitivCorpsRefreshRequest'}
                    type={TextButtonType.Primary}
                    onClick={this.handleRefinitivCorpsRefreshRequest}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_REFINITIV_CORPS_REFRESH_REQUEST)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionPurgeEntity'}
                    type={TextButtonType.Primary}
                    onClick={this.handlePurgeEntity}>
                    {Locale.translate(Translation.TC_ACTION_PURGE_ENTITY)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionCollateralMigration'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerCollateralMigration}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_COLLATERAL_MIGRATION)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionCollateralClearTradeDate'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerCollateralClearTradeDate}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_COLLATERAL_CLEAR_TRADE_DATE)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionCollateralGenerateScheduleReport'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerGenerateScheduleReport}>
                    {Locale.translate(Translation.TC_ACTION_TRIGGER_GENERATE_SCHEDULE_REPORT)}
                  </BigButton>
                </ButtonStrip>
              </>
            )}
            {this.props.canSendApplicationSignals && (
              <>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionResetTradeData'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerResetTestData}>
                    {Locale.translate(Translation.TC_ACTION_RESET_TEST_DATA)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionResetTradeData'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerEodPurge}>
                    {Locale.translate(Translation.TC_ACTION_EOD_PURGE)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionPurgeTradeData'}
                    type={TextButtonType.Primary}
                    onClick={this.handleTriggerPurgeTradeData}>
                    {Locale.translate(Translation.TC_ACTION_PURGE_TRADE_DATA)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionSetCurrentTradeDate'}
                    type={TextButtonType.Primary}
                    onClick={this.handleSetCurrentTradeDate}>
                    {Locale.translate(Translation.TC_ACTION_SET_CURRENT_TRADE_DATE)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionSetCurrentTradeDateToPreviousBusinessDay'}
                    type={TextButtonType.Primary}
                    onClick={this.handleSetCurrentTradeDateToPreviousBusinessDay}>
                    {Locale.translate(Translation.TC_ACTION_SET_CURRENT_TRADE_DATE_TO_PREVIOUS_BUSINESS_DAY)}
                  </BigButton>
                </ButtonStrip>
                <ButtonStrip>
                  <BigButton
                    dataTestId={'OrkSettings-BigButton-ActionSkipHolidays'}
                    type={TextButtonType.Primary}
                    onClick={this.handleSkipHolidays}>
                    {Locale.translate(Translation.TC_ACTION_SKIP_HOLIDAYS)}
                  </BigButton>
                </ButtonStrip>
              </>
            )}
          </Column>
        </Row>
      </Container>
    )
  }

  private handleTriggerOrkDump = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_TRIGGER_ORK_DUMP),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_ORK_DUMP),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerOrkDump]
      }
    })
  }

  private handleRefinitivCollateralRequest = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_REFINITIV_COLLATERAL_REQUEST),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_REFINITIV_COLLATERAL_REQUEST),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerRefinitivCollateralRequest]
      }
    })
  }

  private handleRefinitivRicCodeRequest = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_REFINITIV_RIC_CODE_REQUEST),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_REFINITIV_RIC_CODE_REQUEST),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerRefinitivRicCodeRequest]
      }
    })
  }

  private handleRefinitivIssuerOrgRefreshRequest = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_REFINITIV_ISSUER_REFRESH_REQUEST),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_REFINITIV_ISSUER_REFRESH_REQUEST),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerRefinitivIssuerOrgRequest]
      }
    })
  }

  private handleRefinitivCollateralNewFieldsRequest = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_REFINITIV_NEW_FIELDS_REQUEST),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_REFINITIV_NEW_FIELDS_REQUEST),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerRefinitivNewFieldsRequest]
      }
    })
  }

  private handleRefinitivCorpsRefreshRequest = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_REFINITIV_CORPS_REFRESH_REQUEST),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_REFINITIV_CORPS_REFRESH_REQUEST),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerRefinitivCorpsRefreshRequest]
      }
    })
  }

  private handleTriggerTransactionReportingDump = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_TRIGGER_TRANSACTION_REPORTING_DUMP),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_TRANSACTION_REPORTING_DUMP),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerTransactionReportingDump]
      }
    })
  }

  private handleTriggerNegotiationExportDump = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_TRIGGER_NEGOTIATION_EXPORT_DUMP),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_NEGOTIATION_EXPORT_DUMP),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerNegotiationExportDump]
      }
    })
  }

  private handleTriggerRefinitivImportReport = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_TRIGGER_REFINITIV_IMPORT_REPORT),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_REFINITIV_IMPORT_REPORT),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerRefinitivImportReport]
      }
    })
  }

  private handleTriggerDealExportDump = () => {
    this.props.showModal(<PermissionsReportDialog />)
  }

  private handlePurgeEntity = () => {
    this.props.showModal(<PurgeEntityDialog />)
  }

  private handlePermissionReport = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_TRIGGER_DEAL_EXPORT_DUMP),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_DEAL_EXPORT_DUMP),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerDealExportDump]
      }
    })
  }

  private handleTriggerSotw = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TITLE_TRIGGER_SOTW),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_SOTW),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerSotw]
      }
    })
  }

  private handleTriggerCalculateEndConsideration = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_CALCULATE_END_CONSIDERATION),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_CALCULATE_END_CONSIDERATION),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerCalculateEndConsideration]
      }
    })
  }

  private handleTriggerCompleteDeals = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_COMPLETE_DEALS),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_COMPLETE_DEALS),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerReloadAndCompleteDeals]
      }
    })
  }
  private handleTriggerFailedExportsRetry = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_FAILED_EXPORTS_RETRY),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_FAILED_EXPORTS_RETRY),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerFailedExportsRetry]
      }
    })
  }

  private handleTriggerFailedExportsDelete = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_FAILED_EXPORTS_DELETE),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_FAILED_EXPORTS_DELETE),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerFailedExportsDelete]
      }
    })
  }

  private handleTriggerPurgeTradeData = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_PURGE_TRADE_DATA),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_PURGE_TRADE_DATA),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerPurgeTradeData]
      }
    })
  }

  private handleTriggerResetTestData = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_RESET_TEST_DATA),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_RESET_TEST_DATA),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerResetTestData]
      }
    })
  }

  private handleTriggerEodPurge = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_EOD_PURGE),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_EOD_PURGE),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerEodPurge]
      }
    })
  }

  private handleTriggerFirdsReport = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_FIRDS_REPORT),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_FIRDS_REPORT),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerFirdsReport]
      }
    })
  }

  private handleTriggerBondNoteUpdate = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_BOND_NOTE_UPDATE),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_BOND_NOTE_UPDATE),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerBondNoteUpdate]
      }
    })
  }

  private handleTriggerIncrementIds = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_INCREMENT_IDS),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_INCREMENT_IDS),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerIncrementIds]
      }
    })
  }

  private handleTriggerOktaUsersReport = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_OKTA_USERS_REPORT),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_OKTA_USERS_REPORT),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerOktaUsersReport]
      }
    })
  }

  private handleTriggerUpdateMortgageFactors = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_UPDATE_MORTGAGE_FACTORS),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_UPDATE_MORTGAGE_FACTORS),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerUpdateMortgageFactors]
      }
    })
  }

  private handleTriggerCollateralMigration = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_COLLATERAL_MIGRATION),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_COLLATERAL_MIGRATION),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerCollateralMigration]
      }
    })
  }

  private handleTriggerCollateralClearTradeDate = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_COLLATERAL_CLEAR_TRADE_DATE),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_COLLATERAL_CLEAR_TRADE_DATE),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerCollateralClearTradeDate]
      }
    })
  }

  private handleTriggerGenerateScheduleReport = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_GENERATE_SCHEDULE_REPORT),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_GENERATE_SCHEDULE_REPORT),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.triggerGenerateScheduleReport]
      }
    })
  }

  private handleSetCurrentTradeDate = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_SET_CURRENT_TRADE_DATE),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_SET_CURRENT_TRADE_DATE),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.setCurrentTradeDate]
      }
    })
  }

  private handleSetCurrentTradeDateToPreviousBusinessDay = () => {
    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_SET_CURRENT_TRADE_DATE_TO_PREVIOUS_BUSINESS_DAY),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_SET_CURRENT_TRADE_DATE_TO_PREVIOUS_BUSINESS_DAY),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [DevSignalActions.setCurrentTradeDateToPreviousBusinessDay]
      }
    })
    console.log(new Date(new Date(Date.UTC(new Date().getUTCDate(), new Date().getUTCMonth(), new Date().getUTCFullYear(), 0, 1))))
  }

  private handleSkipHolidays = () => {
    const { allHolidayDates } = this.props
    const nextBusinessDate = getNextAvailableBusinessDate(allHolidayDates)

    this.props.onShowDialog({
      type: NotificationType.Confirm,
      titleText: Locale.translate(Translation.TC_PROMPT_TRIGGER_SKIP_HOLIDAY_DATES),
      messageText: Locale.translate(Translation.SC_PROMPT_MSG_TRIGGER_SKIP_HOLIDAY_DATES),
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO,
      responseMap: {
        [Translation.TC_BUTTON_YES]: [
          DevSignalActions.skipHolidays(SignalType.SET_CURRENT_TRADE_DATE, undefined, undefined, nextBusinessDate)
        ]
      }
    })
  }
}

export const OrderRecordKeepingSettings = withModal(connect(mapStateToProps, mapDispatchToProps)(OrderRecordKeepingSettingsComponent))
